import { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  User, Target, HeartPulse, CreditCard, Camera, Loader2, X, Check,
  AlertCircle, Save, Pencil,
} from "lucide-react";

const GENDERS = ["Masculino", "Feminino", "Outros"];
const FREQUENCIES = ["1x por semana", "2x por semana", "3x por semana", "4x por semana", "5x por semana"];
const GOALS = [
  { key: "Perda de peso", testid: "goal-checkbox-perda-de-peso" },
  { key: "Ganho de massa", testid: "goal-checkbox-ganho-de-massa" },
  { key: "Saúde", testid: "goal-checkbox-saude" },
  { key: "Personalizado", testid: "goal-checkbox-personalizado" },
];
const PAYMENT_METHODS = ["Pix", "Dinheiro", "Crédito à Vista", "Crédito Parcelado", "Bonificado"];

const emptyForm = {
  full_name: "", phone: "", nickname: "", birth_date: "", gender: "", weekly_frequency: "",
  goals: [], custom_goal: "", email: "", has_health_restriction: false,
  health_restriction_text: "", photo_path: null, payment_control_enabled: false,
  payment_method: "", payments: [],
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5 sm:p-7">
    <h2 className="text-lg sm:text-xl font-semibold font-heading text-emerald-400 flex items-center gap-2.5 mb-6">
      <span className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </span>
      {title}
    </h2>
    {children}
  </div>
);

const FieldError = ({ msg }) =>
  msg ? (
    <p className="flex items-center gap-1.5 text-xs text-red-400 mt-1.5">
      <AlertCircle className="w-3.5 h-3.5" /> {msg}
    </p>
  ) : null;

export default function StudentForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(() => {
    const base = initial ? { ...emptyForm, ...initial } : emptyForm;
    return {
      ...base,
      full_name: base.full_name || "", phone: base.phone || "", nickname: base.nickname || "",
      birth_date: base.birth_date || "", email: base.email || "",
      custom_goal: base.custom_goal || "", health_restriction_text: base.health_restriction_text || "",
    };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payDraft, setPayDraft] = useState({ date: "", method: "", amount: "", parcelasTotal: "", hasEntry: false, entryVia: "", entryDate: "", entryAmount: "" });
  const fileRef = useRef();
  const editing = Boolean(initial?.id);

  const formatBRL = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const formatDateBR = (iso) => {
    if (!iso) return "--/--/----";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };
  const sortedPayments = [...(form.payments || [])]
    .map((p, i) => ({ ...p, _k: p._k ?? `${p.date}-${i}` }))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const payLabel = (p) => p.is_entry ? `Entrada (${p.entry_via})` : (p.installment_total ? `Crédito Parcelado — Parcela ${p.installment_n}/${p.installment_total}` : p.method);
  const todayISO = new Date().toISOString().slice(0, 10);
  const isReceived = (p) => {
    if (p.installment_total && !p.is_entry) return Boolean(p.date && p.date <= todayISO);
    return (p.received === undefined || p.received === null) ? Boolean(p.date && p.date <= todayISO) : p.received;
  };
  const payStatus = (p) => {
    if (p.method === "Bonificado") return { key: "bonificado", label: "Bonificado", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" };
    if (p.installment_total && !p.is_entry) {
      return (p.date && p.date <= todayISO)
        ? { key: "pago", label: "Pago", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" }
        : { key: "avencer", label: "A vencer", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
    }
    if (isReceived(p)) return { key: "pago", label: "Pago", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
    if (p.date && p.date < todayISO) return { key: "vencido", label: "Vencido", cls: "bg-red-500/15 text-red-400 border-red-500/30" };
    return { key: "avencer", label: "A vencer", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  };
  const toggleReceived = (k) => setForm((f) => ({ ...f, payments: (f.payments || []).map((p, i) => {
    if ((p._k ?? `${p.date}-${i}`) !== k) return p;
    if (p.installment_total && !p.is_entry) return p;
    return { ...p, received: !isReceived(p) };
  }) }));
  const addMonthsISO = (iso, m) => { const [y, mo, d] = iso.split("-").map(Number); const dt = new Date(y, mo - 1 + m, d); return dt.toISOString().slice(0, 10); };
  const resetPayDraft = () => setPayDraft({ date: "", method: "", amount: "", parcelasTotal: "", hasEntry: false, entryVia: "", entryDate: "", entryAmount: "" });

  const addPayment = () => {
    const rand = () => Math.random().toString(36).slice(2);
    if (payDraft.method === "Crédito Parcelado") {
      const total = parseInt(payDraft.parcelasTotal, 10);
      const val = parseFloat(payDraft.amount);
      if (!payDraft.date || !total || total < 1 || !val || val <= 0) {
        toast.error("Informe a data da 1ª parcela, a quantidade e o valor da parcela.");
        return;
      }
      const recs = [];
      if (payDraft.hasEntry) {
        const ev = parseFloat(payDraft.entryAmount);
        if (!payDraft.entryDate || !payDraft.entryVia || !ev || ev <= 0) {
          toast.error("Preencha data, forma e valor (maior que zero) da entrada.");
          return;
        }
        recs.push({ date: payDraft.entryDate, method: "Crédito Parcelado", is_entry: true, entry_via: payDraft.entryVia, amount: ev, received: true, _k: rand() });
      }
      for (let i = 1; i <= total; i++)
        recs.push({ date: addMonthsISO(payDraft.date, i - 1), method: "Crédito Parcelado", installment_n: i, installment_total: total, amount: val, received: false, _k: rand() });
      setForm((f) => ({ ...f, payments: [...(f.payments || []), ...recs] }));
      resetPayDraft();
      setErrors((e) => ({ ...e, payments: undefined }));
      return;
    }
    if (payDraft.method === "Bonificado") {
      if (!payDraft.date) {
        toast.error("Informe a data do bonificado.");
        return;
      }
      setForm((f) => ({ ...f, payments: [...(f.payments || []), { date: payDraft.date, method: "Bonificado", amount: 0, received: true, _k: rand() }] }));
      resetPayDraft();
      setErrors((e) => ({ ...e, payments: undefined }));
      return;
    }
    if (!payDraft.date || !payDraft.method || payDraft.amount === "" || parseFloat(payDraft.amount) <= 0) {
      toast.error("Preencha Data, Forma e um Valor maior que zero.");
      return;
    }
    setForm((f) => ({ ...f, payments: [...(f.payments || []), { date: payDraft.date, method: payDraft.method, amount: parseFloat(payDraft.amount), received: true, _k: rand() }] }));
    resetPayDraft();
    setErrors((e) => ({ ...e, payments: undefined }));
  };
  const removePayment = (k) => setForm((f) => ({ ...f, payments: (f.payments || []).filter((p, i) => (p._k ?? `${p.date}-${i}`) !== k) }));

  const [editPay, setEditPay] = useState(null);
  const startEditPay = (p) => setEditPay({ k: p._k, date: p.date || "", amount: String(p.amount ?? "") });
  const cancelEditPay = () => setEditPay(null);
  const saveEditPay = () => {
    const val = parseFloat(editPay.amount);
    if (!editPay.date || !val || val <= 0) {
      toast.error("Informe uma data e um valor maior que zero.");
      return;
    }
    setForm((f) => ({
      ...f,
      payments: (f.payments || []).map((p, i) =>
        (p._k ?? `${p.date}-${i}`) === editPay.k ? { ...p, date: editPay.date, amount: val } : p
      ),
    }));
    setEditPay(null);
  };

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleGoal = (key) => {
    setForm((f) => {
      const has = f.goals.includes(key);
      const goals = has ? f.goals.filter((g) => g !== key) : [...f.goals, key];
      return { ...f, goals, custom_goal: goals.includes("Personalizado") ? f.custom_goal : "" };
    });
    setErrors((e) => ({ ...e, goals: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.full_name.trim()) err.full_name = "Nome completo é obrigatório";
    if (!form.phone.trim()) err.phone = "Telefone celular é obrigatório";
    if (!form.nickname.trim()) err.nickname = "Apelido é obrigatório";
    if (!form.gender) err.gender = "Selecione o sexo";
    if (!form.weekly_frequency) err.weekly_frequency = "Selecione a frequência semanal";
    if (form.goals.length === 0) err.goals = "Selecione ao menos um objetivo";
    if (form.goals.includes("Personalizado") && !form.custom_goal.trim())
      err.custom_goal = "Descreva o objetivo personalizado";
    if (form.has_health_restriction && !form.health_restriction_text.trim())
      err.health_restriction_text = "Descreva a restrição de saúde";
    if (form.payment_control_enabled && (form.payments || []).some((p) => !p.date || !p.method || p.amount === "" || p.amount === null || p.amount === undefined))
      err.payments = "Cada pagamento precisa de data, forma e valor";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_path", data.photo_path);
      toast.success("Foto enviada!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Corrija os campos destacados em vermelho.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, payments: form.payment_control_enabled ? (form.payments || []).map(({ _k, ...p }) => p) : [], payment_method: null };
      if (editing) await api.put(`/students/${initial.id}`, payload);
      else await api.post("/students", payload);
      toast.success("Salvo com sucesso!");
      if (!editing) setForm(emptyForm);
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.field_errors) setErrors(detail.field_errors);
      toast.error("Não foi possível salvar. Verifique os campos.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = (field) =>
    `h-12 bg-[#0F1520] text-slate-100 rounded-xl focus-visible:ring-emerald-500 ${
      errors[field] ? "border-red-500 focus-visible:ring-red-500" : "border-[#263147]"
    }`;

  const photoUrl = form.photo_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/photo/${form.photo_path}`
    : null;

  return (
    <form onSubmit={submit} className="space-y-6 max-w-3xl pb-24">
      {/* Dados Pessoais */}
      <Section icon={User} title="Dados Pessoais">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">Nome completo *</Label>
            <Input data-testid="student-full-name-input" value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)} className={inputCls("full_name")} placeholder="Ex: João da Silva" />
            <FieldError msg={errors.full_name} />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Telefone celular *</Label>
            <Input data-testid="student-phone-input" value={form.phone}
              onChange={(e) => set("phone", e.target.value)} className={inputCls("phone")} placeholder="(11) 99999-9999" />
            <FieldError msg={errors.phone} />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Apelido *</Label>
            <Input data-testid="student-nickname-input" value={form.nickname}
              onChange={(e) => set("nickname", e.target.value)} className={inputCls("nickname")} placeholder="Ex: Jão" />
            <FieldError msg={errors.nickname} />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Data de nascimento <span className="text-slate-500">(opcional)</span></Label>
            <Input type="date" data-testid="student-birthdate-input" value={form.birth_date}
              onChange={(e) => set("birth_date", e.target.value)} className={inputCls("birth_date")} />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Sexo *</Label>
            <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger data-testid="student-gender-select" className={inputCls("gender")}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <FieldError msg={errors.gender} />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Frequência semanal *</Label>
            <Select value={form.weekly_frequency} onValueChange={(v) => set("weekly_frequency", v)}>
              <SelectTrigger data-testid="student-frequency-select" className={inputCls("weekly_frequency")}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                {FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <FieldError msg={errors.weekly_frequency} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">E-mail <span className="text-slate-500">(opcional — usado para o aluno entrar com Google)</span></Label>
            <Input data-testid="student-email-input" type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)} className={inputCls("email")} placeholder="aluno@email.com" />
          </div>
          {/* Foto */}
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">Foto do aluno <span className="text-slate-500">(opcional)</span></Label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-2xl bg-[#0F1520] border border-[#263147] overflow-hidden flex items-center justify-center shrink-0">
                {photoUrl ? (
                  <img src={photoUrl} alt="Foto do aluno" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-7 h-7 text-slate-600" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" data-testid="photo-upload-input" onChange={handleFile} className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="h-11 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                  {photoUrl ? "Trocar foto" : "Enviar foto"}
                </Button>
                {photoUrl && (
                  <Button type="button" variant="ghost" onClick={() => set("photo_path", null)}
                    className="h-11 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Objetivos */}
      <Section icon={Target} title="Objetivos do Aluno">
        <p className="text-sm text-slate-400 mb-4">Selecione um ou mais objetivos *</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {GOALS.map((g) => {
            const active = form.goals.includes(g.key);
            return (
              <button key={g.key} type="button" data-testid={g.testid} onClick={() => toggleGoal(g.key)}
                className={`relative h-20 rounded-2xl border text-sm font-medium px-3 transition-all ${
                  active
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                    : "border-[#263147] bg-[#0F1520] text-slate-300 hover:border-[#34435f]"
                }`}>
                {active && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#0A0D12]" />
                  </span>
                )}
                {g.key}
              </button>
            );
          })}
        </div>
        <FieldError msg={errors.goals} />
        {form.goals.includes("Personalizado") && (
          <div className="mt-4 space-y-2 animate-fade-up">
            <Label className="text-slate-300">Descreva o objetivo personalizado *</Label>
            <Textarea data-testid="goal-custom-text-input" value={form.custom_goal}
              onChange={(e) => set("custom_goal", e.target.value)}
              className={`bg-[#0F1520] text-slate-100 rounded-xl min-h-[90px] focus-visible:ring-emerald-500 ${
                errors.custom_goal ? "border-red-500" : "border-[#263147]"
              }`} placeholder="Ex: preparação para maratona, reabilitação..." />
            <FieldError msg={errors.custom_goal} />
          </div>
        )}
      </Section>

      {/* Saúde */}
      <Section icon={HeartPulse} title="Saúde e Restrições">
        <div className="flex items-center justify-between bg-[#0F1520] border border-[#263147] rounded-2xl px-5 py-4">
          <div>
            <p className="text-slate-200 font-medium">Tem restrição de saúde?</p>
            <p className="text-xs text-slate-500 mt-0.5">Lesões, condições, limitações</p>
          </div>
          <Switch data-testid="health-restriction-toggle" checked={form.has_health_restriction}
            onCheckedChange={(v) => set("has_health_restriction", v)}
            className="data-[state=checked]:bg-emerald-500" />
        </div>
        {form.has_health_restriction && (
          <div className="mt-4 space-y-2 animate-fade-up">
            <Label className="text-slate-300">Descreva a restrição *</Label>
            <Textarea data-testid="health-restriction-text-input" value={form.health_restriction_text}
              onChange={(e) => set("health_restriction_text", e.target.value)}
              className={`bg-[#0F1520] text-slate-100 rounded-xl min-h-[90px] focus-visible:ring-emerald-500 ${
                errors.health_restriction_text ? "border-red-500" : "border-[#263147]"
              }`} placeholder="Ex: hérnia de disco, hipertensão..." />
            <FieldError msg={errors.health_restriction_text} />
          </div>
        )}
      </Section>

      {/* Pagamento */}
      <Section icon={CreditCard} title="Controle de Pagamento">
        <div className="flex items-center justify-between bg-[#0F1520] border border-[#263147] rounded-2xl px-5 py-4">
          <div>
            <p className="text-slate-200 font-medium">Ativar controle de pagamentos?</p>
            <p className="text-xs text-slate-500 mt-0.5">Registre a forma de pagamento do aluno</p>
          </div>
          <Switch data-testid="payment-control-toggle" checked={form.payment_control_enabled}
            onCheckedChange={(v) => set("payment_control_enabled", v)}
            className="data-[state=checked]:bg-emerald-500" />
        </div>
        {form.payment_control_enabled && (
          <div className="mt-4 space-y-4 animate-fade-up">
            <div className="bg-[#0F1520] border border-[#263147] rounded-xl p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:items-end">
                <div className="space-y-1">
                  <Label className="text-slate-300 text-xs">Data de Pagamento *</Label>
                  <Input type="date" data-testid="payment-date-input" value={payDraft.date} onChange={(e) => setPayDraft((d) => ({ ...d, date: e.target.value }))} className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-300 text-xs">Forma de Pagamento *</Label>
                  <Select value={payDraft.method} onValueChange={(v) => setPayDraft((d) => ({ ...d, method: v }))}>
                    <SelectTrigger data-testid="payment-method-select" className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                      {PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-300 text-xs">{payDraft.method === "Crédito Parcelado" ? "Valor por parcela (R$) *" : "Valor (R$) *"}</Label>
                  <Input type="number" step="0.01" min="0" data-testid="payment-amount-input" value={payDraft.amount} onChange={(e) => setPayDraft((d) => ({ ...d, amount: e.target.value }))} placeholder="0,00" className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg" />
                </div>
              </div>

              {payDraft.method === "Crédito Parcelado" && (
                <div className="space-y-3 animate-fade-up border-t border-[#263147] pt-3">
                  <div className="space-y-1 sm:max-w-[220px]">
                    <Label className="text-slate-300 text-xs">Quantidade de parcelas *</Label>
                    <Input type="number" step="1" min="1" data-testid="payment-installments-input" value={payDraft.parcelasTotal} onChange={(e) => setPayDraft((d) => ({ ...d, parcelasTotal: e.target.value }))} placeholder="Ex: 5" className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg" />
                    <p className="text-[11px] text-slate-500">A 1ª parcela usa a data acima; as demais são geradas mensalmente.</p>
                  </div>
                  <div className="flex items-center justify-between bg-[#181F2C] border border-[#263147] rounded-lg px-3 py-2.5">
                    <div>
                      <p className="text-slate-200 text-sm font-medium">Houve entrada?</p>
                      <p className="text-[11px] text-slate-500">Registre uma entrada inicial separada</p>
                    </div>
                    <Switch data-testid="payment-entry-toggle" checked={payDraft.hasEntry} onCheckedChange={(v) => setPayDraft((d) => ({ ...d, hasEntry: v }))} className="data-[state=checked]:bg-emerald-500" />
                  </div>
                  {payDraft.hasEntry && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:items-end animate-fade-up">
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Forma da entrada *</Label>
                        <Select value={payDraft.entryVia} onValueChange={(v) => setPayDraft((d) => ({ ...d, entryVia: v }))}>
                          <SelectTrigger data-testid="payment-entry-via-select" className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg"><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                            {["Pix", "Débito", "Dinheiro"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Data da entrada *</Label>
                        <Input type="date" data-testid="payment-entry-date-input" value={payDraft.entryDate} onChange={(e) => setPayDraft((d) => ({ ...d, entryDate: e.target.value }))} className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-slate-300 text-xs">Valor da entrada (R$) *</Label>
                        <Input type="number" step="0.01" min="0" data-testid="payment-entry-amount-input" value={payDraft.entryAmount} onChange={(e) => setPayDraft((d) => ({ ...d, entryAmount: e.target.value }))} placeholder="0,00" className="h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button type="button" data-testid="payment-add-button" onClick={addPayment} className="w-full sm:w-auto h-11 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-4">Adicionar</Button>
            </div>
            <FieldError msg={errors.payments} />
            {sortedPayments.length > 0 ? (
              <div className="space-y-1.5" data-testid="payment-history">
                {sortedPayments.map((p) => (
                  editPay && editPay.k === p._k ? (
                    <div key={p._k} data-testid="payment-row-edit" className="flex flex-wrap items-center gap-2 bg-[#0F1520] border border-emerald-500/40 rounded-lg px-3 py-2 text-sm">
                      <Input type="date" data-testid="payment-edit-date-input" value={editPay.date} onChange={(e) => setEditPay((d) => ({ ...d, date: e.target.value }))} className="h-9 w-[150px] bg-[#181F2C] border-[#263147] text-slate-100 rounded-md" />
                      <span className="text-slate-400 text-xs truncate max-w-[140px]">{payLabel(p)}</span>
                      <Input type="number" step="0.01" min="0" data-testid="payment-edit-amount-input" value={editPay.amount} onChange={(e) => setEditPay((d) => ({ ...d, amount: e.target.value }))} placeholder="0,00" className="h-9 w-[110px] bg-[#181F2C] border-[#263147] text-slate-100 rounded-md" />
                      <div className="flex gap-1 ml-auto">
                        <Button type="button" data-testid="payment-edit-save-button" onClick={saveEditPay} className="h-8 w-8 p-0 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] rounded-md"><Check className="w-4 h-4" /></Button>
                        <Button type="button" variant="ghost" data-testid="payment-edit-cancel-button" onClick={cancelEditPay} className="h-8 w-8 p-0 text-slate-400 hover:bg-slate-500/10 rounded-md"><X className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ) : (
                    <div key={p._k} data-testid="payment-row" className="flex flex-wrap items-center gap-x-2 gap-y-1 bg-[#0F1520] border border-[#263147] rounded-lg px-3 py-2 text-sm">
                      <span className="text-slate-300 tabular-nums">{formatDateBR(p.date)}</span>
                      <span className="text-slate-500">|</span>
                      <span className="text-slate-200 flex-1 min-w-[80px] truncate">{payLabel(p)}</span>
                      {p.method === "Bonificado"
                        ? <span className="text-blue-400 font-medium">Sem valor</span>
                        : <span className="text-emerald-400 font-semibold tabular-nums">{formatBRL(p.amount)}</span>}
                      {(() => { const st = payStatus(p); return (
                        <button type="button" data-testid="payment-status-badge" title={st.key === "bonificado" ? "Bonificado" : "Clique para alternar pago/pendente"} onClick={() => st.key !== "bonificado" && toggleReceived(p._k)} className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls} ${st.key !== "bonificado" ? "cursor-pointer" : "cursor-default"}`}>{st.label}</button>
                      ); })()}
                      <Button type="button" variant="ghost" data-testid="payment-edit-button" onClick={() => startEditPay(p)} className="h-7 w-7 p-0 text-slate-400 hover:bg-slate-500/10 rounded-md"><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button type="button" variant="ghost" onClick={() => removePayment(p._k)} className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10 rounded-md"><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  )
                ))}
              </div>
            ) : <p className="text-xs text-slate-500">Nenhum pagamento registrado ainda.</p>}
          </div>
        )}
      </Section>

      <div className="flex gap-3 sticky bottom-4">
        <Button type="submit" data-testid="save-student-button" disabled={saving}
          className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold text-base rounded-xl shadow-lg shadow-emerald-500/20">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" /> {editing ? "Salvar alterações" : "Cadastrar aluno"}</>}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}
            className="h-14 px-6 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">
            Cancelar
          </Button>
        )}
      </div>
    </form>
  );
}
